import { v4 as uuidv4 } from 'uuid';
import { httpClient } from './http-client';
import { useAuthStore } from '@/stores/auth.store';

export enum EventType {
  LEARNING = 'learning',
  ENGAGEMENT = 'engagement'
}

export enum LearningEvent {
  TREE_STARTED = 'tree.started',
  TREE_COMPLETED = 'tree.completed',
  TREE_ABANDONED = 'tree.abandoned',

  NODE_OPENED = 'node.opened',
  NODE_COMPLETED = 'node.completed',
  NODE_SKIPPED = 'node.skipped',
  NODE_REVISITED = 'node.revisited',

  CONTENT_VIEWED = 'content.viewed',
  CONTENT_READ_TIME = 'content.read_time',
  CONTENT_SCROLL_DEPTH = 'content.scroll_depth',

  NOTE_CREATED = 'note.created',
  HIGHLIGHT_ADDED = 'highlight.added'
}


export enum EngagementEvent {
  SESSION_START = 'session.start',
  SESSION_END = 'session.end',
  SESSION_RESUME = 'session.resume',

  TREE_CREATED = 'tree.created',
  NODE_CREATED = 'node.created',
  NODE_EXPANDED = 'node.expanded',
  CONTENT_VIEWED = 'content.viewed',
  CONTENT_GENERATION = 'content.generation',
  CONTENT_EDITED = 'content.edited',
  CONTENT_PUBLISHED = 'content.published'
}

export interface ActivityEvent {
  eventType: EventType;
  eventName: string;
  entityType?: string;
  entityId?: string;
  sessionId: string;
  userId?: string;
  deviceInfo?: DeviceInfo;
  location?: LocationInfo;
  referrer?: string;
  properties?: Record<string, unknown>;
  duration?: number;
  value?: number;
  timestamp: Date;
}

interface DeviceInfo {
  userAgent: string;
  platform: string;
  language: string;
  screenResolution: string;
  viewport: string;
  deviceType: 'desktop' | 'mobile' | 'tablet';
}

interface LocationInfo {
  page: string;
  section?: string;
  path?: string;
  coordinates?: { x: number; y: number };
}

interface SessionInfo {
  sessionId: string;
  startTime: Date;
  lastActivityTime: Date;
  pageViews: number;
  events: number;
}

/**
 */
export class ActivityTracker {
  private static instance: ActivityTracker;
  private queue: ActivityEvent[] = [];
  private session: SessionInfo | null = null;
  private readonly batchSize = 50;
  private readonly flushInterval = 5000;
  private readonly sessionTimeout = 30 * 60 * 1000;
  private flushTimer: NodeJS.Timeout | null = null;
  private sessionTimer: NodeJS.Timeout | null = null;

  private constructor() {
    this.initializeSession();
    this.startFlushTimer();
    this.setupEventListeners();
  }

  static getInstance(): ActivityTracker {
    if (!this.instance) {
      this.instance = new ActivityTracker();
    }
    return this.instance;
  }

  /**
   */
  private async initializeSession(): Promise<void> {
    const storedSession = this.getStoredSession();

    if (storedSession && this.isSessionValid(storedSession)) {
      this.session = storedSession;
      this.track(EventType.ENGAGEMENT, EngagementEvent.SESSION_RESUME);
      await this.updateServerSession('update');
    } else {
      await this.createNewSession();
      this.track(EventType.ENGAGEMENT, EngagementEvent.SESSION_START);
    }

    this.resetSessionTimer();
  }

  /**
   */
  private async createNewSession(): Promise<void> {
    this.session = {
      sessionId: uuidv4(),
      startTime: new Date(),
      lastActivityTime: new Date(),
      pageViews: 0,
      events: 0
    };
    this.storeSession();

    await this.updateServerSession('start');
  }

  /**
   */
  private isSessionValid(session: SessionInfo): boolean {
    const now = new Date().getTime();
    const lastActivity = new Date(session.lastActivityTime).getTime();
    return (now - lastActivity) < this.sessionTimeout;
  }

  /**
   */
  private storeSession(): void {
    if (this.session) {
      localStorage.setItem('activity_session', JSON.stringify(this.session));
    }
  }

  /**
   */
  private getStoredSession(): SessionInfo | null {
    const stored = localStorage.getItem('activity_session');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return null;
      }
    }
    return null;
  }

  /**
   */
  private resetSessionTimer(): void {
    if (this.sessionTimer) {
      clearTimeout(this.sessionTimer);
    }

    this.sessionTimer = setTimeout(() => {
      this.endSession();
    }, this.sessionTimeout);
  }

  /**
   */
  private async endSession(): Promise<void> {
    if (this.session) {
      await this.track(EventType.ENGAGEMENT, EngagementEvent.SESSION_END, {
        duration: new Date().getTime() - new Date(this.session.startTime).getTime()
      });
      await this.flush();

      await this.updateServerSession('end');

      this.session = null;
      localStorage.removeItem('activity_session');
    }
  }

  /**
   */
  private async updateServerSession(action: 'start' | 'end' | 'update'): Promise<void> {
    if (!this.session) return;

    const userId = this.getUserId();
    if (!userId) {
      return;
    }

    try {
      const data: { deviceInfo: DeviceInfo; userAgent: string } = {
        deviceInfo: this.getDeviceInfo(),
        userAgent: navigator.userAgent,
      };

      await httpClient.post<void>('/api/v1/analytics/session', {
        sessionId: this.session.sessionId,
        action,
        data
      });
    } catch (error) {
      console.error('Failed to update server session:', error);
    }
  }

  /**
   */
  track(
    eventType: EventType,
    eventName: string,
    options?: {
      entityType?: string;
      entityId?: string;
      properties?: Record<string, unknown>;
      duration?: number;
      value?: number;
    }
  ): void {
    if (!this.session) {
      this.createNewSession();
    }

    const event: ActivityEvent = {
      eventType,
      eventName,
      entityType: options?.entityType,
      entityId: options?.entityId,
      sessionId: this.session!.sessionId,
      userId: this.getUserId(),
      deviceInfo: this.getDeviceInfo(),
      location: this.getLocationInfo(),
      referrer: document.referrer,
      properties: options?.properties,
      duration: options?.duration,
      value: options?.value,
      timestamp: new Date()
    };

    this.queue.push(event);

    if (this.session) {
      this.session.events++;
      this.session.lastActivityTime = new Date();
      this.storeSession();
    }

    this.resetSessionTimer();

    if (this.queue.length >= this.batchSize) {
      this.flush();
    }
  }

  /**
   */
  trackPageView(page: string, section?: string): void {
    if (this.session) {
      this.session.pageViews++;
      this.storeSession();
    }
  }

  /**
   */
  startTimeTracking(entityType: string, entityId: string): () => void {
    const startTime = Date.now();

    return () => {
      const duration = Date.now() - startTime;
      this.track(EventType.LEARNING, LearningEvent.CONTENT_READ_TIME, {
        entityType,
        entityId,
        duration
      });
    };
  }

  /**
   */
  private startFlushTimer(): void {
    this.flushTimer = setInterval(() => {
      if (this.queue.length > 0) {
        this.flush();
      }
    }, this.flushInterval);
  }

  /**
   */
  async flush(): Promise<void> {
    if (this.queue.length === 0) return;

    const userId = this.getUserId();
    if (!userId) {
      this.queue = [];
      return;
    }

    const events = [...this.queue];
    this.queue = [];

    try {
      await httpClient.post<void>('/api/v1/analytics/events', {
        events,
        sessionId: this.session?.sessionId
      });
    } catch (error: unknown) {
      console.error('Failed to send activity events:', error);
      const isUnauthorized =
        (error instanceof Error && 'response' in error && (error as { response?: { status?: number } }).response?.status === 401);
      if (!isUnauthorized && this.queue.length < this.batchSize * 2) {
        this.queue.unshift(...events);
      }
    }
  }

  /**
   */
  private getUserId(): string | undefined {
    const authState = useAuthStore.getState();
    return authState.user?.id;
  }

  /**
   */
  private getDeviceInfo(): DeviceInfo {
    return {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      screenResolution: `${screen.width}x${screen.height}`,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      deviceType: this.detectDeviceType()
    };
  }

  /**
   */
  private detectDeviceType(): 'desktop' | 'mobile' | 'tablet' {
    const userAgent = navigator.userAgent.toLowerCase();
    if (/mobile/i.test(userAgent)) return 'mobile';
    if (/tablet|ipad/i.test(userAgent)) return 'tablet';
    return 'desktop';
  }

  /**
   */
  private getLocationInfo(): LocationInfo {
    return {
      page: window.location.pathname,
      path: window.location.href
    };
  }

  /**
   */
  private setupEventListeners(): void {
    window.addEventListener('beforeunload', () => {
      this.endSession();
    });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.flush();
      } else {
        this.resetSessionTimer();
      }
    });
  }

  /**
   */
  destroy(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }
    if (this.sessionTimer) {
      clearTimeout(this.sessionTimer);
    }
    this.flush();
  }
}

export const activityTracker = ActivityTracker.getInstance();